import { Observable, Subject } from 'rxjs';
import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, ElementRef, OnDestroy, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ArchitecturalPlanPage } from './models/architectural-plan-page.model';
import { PdfCacheService } from './pdf-cache.service';
import { PDF_RENDER_LIMITS, PdfRenderPlan, planPdfRender } from './pdf-render-budget';
import { anchoredScrollPosition, CssPoint, scrollportCenter } from './pdf-scrollport';

export type PdfRenderFailureKind = 'authentication' | 'authorization' | 'not-found' | 'unavailable' | 'unknown';

export interface PdfRenderFailure {
  kind: PdfRenderFailureKind;
  message: string;
}

/** Maps typed RPC failures to stable, non-sensitive Preview messages. */
export function pdfRenderFailure(error: unknown): PdfRenderFailure {
  const code =
    typeof error === 'object' && error !== null && typeof (error as { code?: unknown }).code === 'number'
      ? (error as { code: number }).code
      : null;
  const name = error instanceof Error ? error.name : '';

  if (name === 'RpcAuthenticationError' || code === 16) {
    return { kind: 'authentication', message: 'Your sign-in expired. Sign in again, then retry the PDF preview.' };
  }
  if (code === 7) {
    return { kind: 'authorization', message: 'You do not have permission to view this PDF.' };
  }
  if (code === 5) {
    return { kind: 'not-found', message: 'This PDF is no longer available. Refresh the project and choose a page.' };
  }
  if (code === 8 || code === 14) {
    return { kind: 'unavailable', message: 'The PDF preview is temporarily unavailable. Try again.' };
  }
  return { kind: 'unknown', message: 'The PDF preview could not be loaded. Try again.' };
}

/**
 * Renders architectural plan PDFs onto an HTML canvas with zoom, pan, and
 * fit-to-viewport controls. NOT providedIn root — each PreviewComponent
 * provides its own instance so concurrent instances (e.g. during page
 * transition animations) each own their own canvas and render state.
 *
 * The expensive parsed-PDF cache lives in the singleton PdfCacheService.
 */

export interface FocalPointInfo {
  targetX: number;
  targetY: number;
  cx: number;
  cy: number;
  oldScale: number;
}

/** Named inputs for preserving the Preview camera while changing zoom. */
export interface PdfZoomOptions {
  focalScreenPoint?: CssPoint;
  canvasPoint?: CssPoint;
  onBeforeSwap?: () => void;
  resolveFocalTarget?: () => CssPoint;
}

/** Manages PDF.js page rendering and canvas interaction for a preview. */
@Injectable()
export class PdfRenderService implements OnDestroy {
  private pdfCacheService = inject(PdfCacheService);
  private snackBar = inject(MatSnackBar);

  readonly pageRendered$ = new Subject<{
    page: ArchitecturalPlanPage | null;
    scale: number;
    dims: { width: number; height: number } | null;
  }>();
  /** Persistent, sanitized failure state consumed by Preview; null clears an earlier failure. */
  readonly renderFailure$ = new Subject<PdfRenderFailure | null>();

  private canvas!: HTMLCanvasElement;
  private container!: HTMLDivElement;
  private currentRenderTask: any = null;
  private currentPdfPage: any = null;
  private currentPdf: any = null;
  private retainedPage: ArchitecturalPlanPage | null = null;
  private static readonly CONTAINER_PADDING = 60;
  private static readonly HI_DPI_FACTOR = 2;
  /** Extra offset added when centering the canvas within the scrollable container. */
  static readonly CENTERING_PADDING_PX = 20;

  private currentScale = 1.0;
  private baseScale = 1.0;
  private zoomAnimationId = 0;
  private renderRequestGeneration = 0;
  private renderGeneration = 0;
  private refinementFrame: number | null = null;
  /** The PDF page whose render is on the canvas now, so a swap knows whether the old picture is comparable. */
  private paintedPdfPage: unknown = null;
  /** The old picture fading out over a just-swapped sharp render (Issue #3245). */
  private swapFadeLayer: HTMLCanvasElement | null = null;
  private swapFadeAnimation: Animation | null = null;
  /** How long the old picture takes to fade out over the sharp render. */
  static readonly SWAP_FADE_MS = 150;
  /** Above this many backing pixels the old picture is not copied for a fade: the copy would cost a long frame. */
  static readonly SWAP_FADE_MAX_PIXELS = 4096 * 4096;
  private lastBudgetClampSignature: string | null = null;

  setCanvas(canvasRef: ElementRef<HTMLCanvasElement>, containerRef: ElementRef<HTMLDivElement>) {
    this.canvas = canvasRef.nativeElement;
    this.container = containerRef.nativeElement;
  }

  async renderPdfForPage(page: ArchitecturalPlanPage | null) {
    this.renderFailure$.next(null);
    if (!this.canvas || !this.container) {
      ClientLoggingService.log('ERROR', 'Canvas or container not set in PdfRenderService.');
      this.renderFailure$.next(pdfRenderFailure(new Error('Preview surface unavailable')));
      return;
    }

    const requestGeneration = ++this.renderRequestGeneration;
    this.cancelCurrentRender();
    this.releaseRetainedPage();
    this.clearCanvas();

    if (!page) {
      return;
    }

    ClientLoggingService.log('INFO', `[PdfRenderService] Rendering PDF for page ${page.getPageNumber()}`);

    try {
      const cached = await this.pdfCacheService.getParsedPage(page, { retain: true });
      if (requestGeneration !== this.renderRequestGeneration) return;
      if (!cached) {
        this.renderFailure$.next(pdfRenderFailure({ code: 5 }));
        return;
      }

      this.currentPdf = cached.pdf;
      this.currentPdfPage = cached.page;
      this.retainedPage = page;
      const rendered = await this.renderFromParsedPage(requestGeneration);
      if (!rendered || requestGeneration !== this.renderRequestGeneration) return;
      this.pageRendered$.next({
        page,
        scale: this.currentScale,
        dims: this.getOriginalPageDimensions(),
      });
      this.scheduleHighResolutionRefinement(requestGeneration);
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error loading PDF in service:', error);
      this.snackBar.open('Error loading PDF preview', 'Close', { duration: 5000 });
      if (requestGeneration === this.renderRequestGeneration) this.renderFailure$.next(pdfRenderFailure(error));
    }
  }

  private getFitScales(): { widthScale: number; heightScale: number } | null {
    if (!this.currentPdfPage || !this.container) {
      return null;
    }
    const containerWidth = this.container.clientWidth - PdfRenderService.CONTAINER_PADDING;
    const containerHeight = this.container.clientHeight - PdfRenderService.CONTAINER_PADDING;
    const viewport = this.currentPdfPage.getViewport({ scale: 1.0 });
    if (
      !Number.isFinite(viewport.width) ||
      !Number.isFinite(viewport.height) ||
      viewport.width <= 0 ||
      viewport.height <= 0 ||
      containerWidth <= 0 ||
      containerHeight <= 0
    ) {
      ClientLoggingService.log('WARN', '[PdfRenderService] Invalid page or container dimensions; render skipped', {
        pageWidth: viewport.width,
        pageHeight: viewport.height,
        containerWidth,
        containerHeight,
      });
      return null;
    }
    return {
      widthScale: (containerWidth / viewport.width) * PdfRenderService.HI_DPI_FACTOR,
      heightScale: (containerHeight / viewport.height) * PdfRenderService.HI_DPI_FACTOR,
    };
  }

  private async renderFromParsedPage(requestGeneration: number): Promise<boolean> {
    if (requestGeneration !== this.renderRequestGeneration) return false;
    const scale = this.getFitScale('viewport');
    if (scale === null) {
      this.renderFailure$.next(pdfRenderFailure(new Error('Preview dimensions unavailable')));
      return false;
    }

    this.baseScale = scale;
    this.currentScale = this.baseScale;

    // Put a CSS-pixel-density canvas on screen first. The full 2× bitmap is refined only after the
    // browser has had a frame to paint this pass, so loading never waits on high-DPI rasterization.
    const rendered = await this.renderCurrentPage(
      requestGeneration,
      undefined,
      this.currentScale / PdfRenderService.HI_DPI_FACTOR
    );
    if (!rendered || requestGeneration !== this.renderRequestGeneration) return false;
    this.centerView();
    return true;
  }

  private async renderCurrentPage(
    requestGeneration: number,
    onReadyToSwap?: () => void,
    renderScale = this.currentScale
  ): Promise<boolean> {
    if (!this.currentPdfPage || !this.canvas) {
      return false;
    }

    const renderGeneration = this.renderGeneration;
    let renderTask: any = null;
    let renderPlan: PdfRenderPlan | null = null;
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    let cancelled = false;
    try {
      renderPlan = this.createRenderPlan(renderScale);
      if (!renderPlan) {
        throw new Error('PDF page has invalid render dimensions');
      }
      const scaledViewport = this.currentPdfPage.getViewport({ scale: renderPlan.effectiveScale });
      const displayViewport = this.currentPdfPage.getViewport({ scale: this.currentScale });

      // Create an offscreen canvas for rendering to prevent blank flashes
      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = renderPlan.outputWidth;
      offscreenCanvas.height = renderPlan.outputHeight;
      const context = offscreenCanvas.getContext('2d');
      if (!context) {
        throw new Error('Could not get canvas context');
      }

      context.setTransform(1, 0, 0, 1, 0, 0);

      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
      };

      renderTask = this.currentPdfPage.render(renderContext);
      this.currentRenderTask = renderTask;
      await renderTask.promise;
      if (requestGeneration !== this.renderRequestGeneration || renderGeneration !== this.renderGeneration) {
        cancelled = true;
        return false;
      }

      // Copy the picture on screen now: resizing the canvas below clears it.
      const fadeLayer = this.captureSwapFadeLayer(renderPlan);
      // A fade still running from an earlier swap would sit over this render at the old size.
      this.cancelSwapFade();

      // Render finished successfully! Now update main canvas synchronously
      this.canvas.width = renderPlan.outputWidth;
      this.canvas.height = renderPlan.outputHeight;
      // Keep the user-facing zoom independent from the backing bitmap. Large
      // pages can exhaust the canvas budget before reaching the useful display
      // zoom limit, so PDF.js renders at the safe scale while CSS presents the
      // bitmap at the requested scale.
      this.canvas.style.width = `${displayViewport.width / PdfRenderService.HI_DPI_FACTOR}px`;
      this.canvas.style.height = `${displayViewport.height / PdfRenderService.HI_DPI_FACTOR}px`;

      // Allow caller to adjust scroll position or CSS transforms BEFORE the browser paints
      if (onReadyToSwap) {
        onReadyToSwap();
      }

      // Copy the fully rendered image to the main canvas
      const mainContext = this.canvas.getContext('2d');
      mainContext?.drawImage(offscreenCanvas, 0, 0);
      this.paintedPdfPage = this.currentPdfPage;
      if (fadeLayer) this.startSwapFade(fadeLayer);
      return true;
    } catch (error: any) {
      cancelled = error?.name === 'RenderingCancelledException';
      if (!cancelled) {
        ClientLoggingService.log('ERROR', 'Error rendering PDF page:', error);
        this.snackBar.open('Error rendering PDF', 'Close', { duration: 5000 });
        if (requestGeneration === this.renderRequestGeneration) this.renderFailure$.next(pdfRenderFailure(error));
      }
      return false;
    } finally {
      if (this.currentRenderTask === renderTask) this.currentRenderTask = null;
      if (renderPlan) {
        const finishedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        ClientLoggingService.log('INFO', '[PdfRenderService] Render metrics', {
          sourceWidth: Math.round(renderPlan.sourceWidth),
          sourceHeight: Math.round(renderPlan.sourceHeight),
          requestedScale: renderPlan.requestedScale,
          effectiveScale: renderPlan.effectiveScale,
          outputWidth: renderPlan.outputWidth,
          outputHeight: renderPlan.outputHeight,
          outputPixels: renderPlan.outputPixels,
          estimatedCanvasBytes: renderPlan.estimatedCanvasBytes,
          budgetClamped: renderPlan.budgetClamped,
          durationMs: Math.max(0, Math.round(finishedAt - startedAt)),
          cancelled,
        });
      }
    }
  }

  clearCanvas(): void {
    this.cancelSwapFade();
    this.paintedPdfPage = null;
    if (this.canvas) {
      const context = this.canvas.getContext('2d');
      if (context) {
        context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      }
    }
  }

  cancelCurrentRender(): void {
    this.renderGeneration++;
    this.cancelSwapFade();
    if (this.refinementFrame !== null) {
      cancelAnimationFrame(this.refinementFrame);
      this.refinementFrame = null;
    }
    if (this.currentRenderTask) {
      this.currentRenderTask.cancel();
      this.currentRenderTask = null;
    }
  }

  /** Removes a fading old picture at once, for a new zoom, gesture, render or page. */
  cancelSwapFade(): void {
    const layer = this.swapFadeLayer;
    if (!layer) return;
    this.swapFadeLayer = null;
    this.swapFadeAnimation?.cancel();
    this.swapFadeAnimation = null;
    layer.remove();
  }

  /**
   * Copies the picture on screen before a sharp render replaces it, so the swap can fade from it
   * instead of switching in one frame (Issue #3245). Returns null when a fade would not show the
   * same picture or would cost too much: another page, a different shape (a rotation), a very large
   * bitmap, or a reader who asked for reduced motion.
   */
  private captureSwapFadeLayer(plan: PdfRenderPlan): HTMLCanvasElement | null {
    const old = this.canvas;
    if (!old?.parentElement || this.paintedPdfPage === null || this.paintedPdfPage !== this.currentPdfPage) return null;
    if (!old.width || !old.height || old.width * old.height > PdfRenderService.SWAP_FADE_MAX_PIXELS) return null;
    const oldAspect = old.width / old.height;
    const newAspect = plan.outputWidth / plan.outputHeight;
    if (Math.abs(oldAspect / newAspect - 1) > 0.01) return null;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return null;

    const layer = document.createElement('canvas');
    layer.width = old.width;
    layer.height = old.height;
    const context = layer.getContext('2d');
    if (!context) return null;
    context.drawImage(old, 0, 0);
    layer.setAttribute('aria-hidden', 'true');
    layer.dataset['testid'] = 'preview-swap-fade';
    return layer;
  }

  /**
   * Lays the copied old picture exactly over the sharp render, which lands where the old picture was
   * drawn, and fades it out. Only opacity animates, which the browser runs off the main thread. The
   * layer follows the canvas in the DOM, so the overlays that follow the canvas's wrapper stay above it.
   */
  private startSwapFade(layer: HTMLCanvasElement): void {
    this.cancelSwapFade();
    const canvas = this.canvas;
    const { left, top } = this.untransformedCanvasPosition(canvas);
    Object.assign(layer.style, {
      position: 'absolute',
      left: `${left}px`,
      top: `${top}px`,
      width: canvas.style.width,
      height: canvas.style.height,
      transform: canvas.style.transform,
      transformOrigin: canvas.style.transformOrigin,
      pointerEvents: 'none',
      margin: '0',
    });
    canvas.after(layer);
    this.swapFadeLayer = layer;
    if (typeof layer.animate !== 'function') {
      this.cancelSwapFade();
      return;
    }
    const animation = layer.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: PdfRenderService.SWAP_FADE_MS,
      easing: 'ease-out',
      fill: 'forwards',
    });
    this.swapFadeAnimation = animation;
    animation.onfinish = () => {
      if (this.swapFadeLayer === layer) this.cancelSwapFade();
    };
  }

  /**
   * Where the canvas is laid out inside its wrapper. offsetLeft and offsetTop round to whole pixels,
   * and a centred canvas often sits on a half pixel, which left the faded picture a pixel off; the
   * boxes give the exact place whenever no transform is drawn on the canvas.
   */
  private untransformedCanvasPosition(canvas: HTMLCanvasElement): { left: number; top: number } {
    const parent = canvas.parentElement;
    const transform = canvas.style.transform;
    if (!parent || (transform && transform !== 'none')) return { left: canvas.offsetLeft, top: canvas.offsetTop };
    const box = canvas.getBoundingClientRect();
    const parentBox = parent.getBoundingClientRect();
    return { left: box.left - parentBox.left - parent.clientLeft, top: box.top - parentBox.top - parent.clientTop };
  }

  /** Refines the first visible bitmap on the next paint frame unless navigation or zoom supersedes it. */
  private scheduleHighResolutionRefinement(requestGeneration: number): void {
    const expectedScale = this.currentScale;
    const expectedRenderGeneration = this.renderGeneration;
    this.refinementFrame = requestAnimationFrame(() => {
      this.refinementFrame = null;
      if (
        requestGeneration !== this.renderRequestGeneration ||
        expectedRenderGeneration !== this.renderGeneration ||
        expectedScale !== this.currentScale
      ) {
        return;
      }
      void this.renderCurrentPage(requestGeneration);
    });
  }

  async zoomIn(): Promise<void> {
    const maximumScale = this.getSafeScale(PDF_RENDER_LIMITS.maxScale);
    if (this.currentScale < maximumScale) {
      const focalInfo = this.getFocalPointInfo();
      this.currentScale = this.getSafeScale(this.currentScale * 1.2);
      this.cancelCurrentRender();
      await this.renderCurrentPage(this.renderRequestGeneration, () => {
        this.preserveFocalPoint(focalInfo, this.currentScale);
      });
    }
  }

  async zoomOut(): Promise<void> {
    if (this.currentScale > 0.2) {
      const focalInfo = this.getFocalPointInfo();
      this.currentScale /= 1.2;
      this.cancelCurrentRender();
      await this.renderCurrentPage(this.renderRequestGeneration, () => {
        this.preserveFocalPoint(focalInfo, this.currentScale);
      });
    }
  }

  /**
   * The scale one fit mode gives the current page in the container at the container's current size,
   * or null when there is no page or no room. It is the scale fitToWidth, fitToHeight and
   * fitToViewport apply, so a caller can preview a fit before rendering it (Issue #2920).
   */
  getFitScale(mode: 'width' | 'height' | 'viewport'): number | null {
    const scales = this.getFitScales();
    if (!scales) return null;
    if (mode === 'width') return this.getSafeScale(scales.widthScale);
    if (mode === 'height') return this.getSafeScale(scales.heightScale);
    const requestedBaseScale = Math.min(scales.widthScale, scales.heightScale);
    return this.getSafeScale(requestedBaseScale, requestedBaseScale);
  }

  /** CSS size in pixels at which the current page is displayed at one scale, or null with no page. */
  getDisplaySize(scale: number): { width: number; height: number } | null {
    if (!this.currentPdfPage) return null;
    const viewport = this.currentPdfPage.getViewport({ scale });
    return {
      width: viewport.width / PdfRenderService.HI_DPI_FACTOR,
      height: viewport.height / PdfRenderService.HI_DPI_FACTOR,
    };
  }

  async fitToWidth(): Promise<void> {
    const scale = this.getFitScale('width');
    if (scale === null) return;

    this.currentScale = scale;
    this.cancelCurrentRender();
    await this.renderCurrentPage(this.renderRequestGeneration, () => {
      this.centerView();
    });
  }

  async fitToHeight(): Promise<void> {
    const scale = this.getFitScale('height');
    if (scale === null) return;

    this.currentScale = scale;
    this.cancelCurrentRender();
    await this.renderCurrentPage(this.renderRequestGeneration, () => {
      this.centerView();
    });
  }

  async fitToViewport(onBeforeSwap?: () => void): Promise<void> {
    const scale = this.getFitScale('viewport');
    if (scale === null) return;

    this.baseScale = scale;
    this.currentScale = this.baseScale;
    this.cancelCurrentRender();
    await this.renderCurrentPage(this.renderRequestGeneration, () => {
      if (onBeforeSwap) {
        onBeforeSwap();
      }
      this.centerView();
    });
  }

  async setZoom(scale: number, options: PdfZoomOptions = {}): Promise<void> {
    const { focalScreenPoint, canvasPoint, onBeforeSwap, resolveFocalTarget } = options;
    const focalInfo = this.getFocalPointInfo(focalScreenPoint?.x, focalScreenPoint?.y, canvasPoint?.x, canvasPoint?.y);

    this.currentScale = this.getSafeScale(scale);
    this.cancelCurrentRender();

    await this.renderCurrentPage(this.renderRequestGeneration, () => {
      onBeforeSwap?.();
      const currentTarget = resolveFocalTarget?.();
      if (focalInfo && currentTarget) {
        focalInfo.targetX = currentTarget.x;
        focalInfo.targetY = currentTarget.y;
      }
      this.preserveFocalPoint(focalInfo, this.currentScale);
    });
  }

  /**
   * Sets scale and explicitly positions the scroll offset before painting.
   * Used for deterministic framing animations and deep links (Issue #1748).
   */
  async setZoomAndScroll(
    scale: number,
    scrollLeft: number,
    scrollTop: number,
    onBeforeSwap?: () => void
  ): Promise<void> {
    this.currentScale = this.getSafeScale(scale);
    this.cancelCurrentRender();

    await this.renderCurrentPage(this.renderRequestGeneration, () => {
      if (this.container) {
        const maxSL = Math.max(0, this.container.scrollWidth - this.container.clientWidth);
        const maxST = Math.max(0, this.container.scrollHeight - this.container.clientHeight);
        this.container.scrollLeft = Math.max(0, Math.min(maxSL, scrollLeft));
        this.container.scrollTop = Math.max(0, Math.min(maxST, scrollTop));
      }
      if (onBeforeSwap) {
        onBeforeSwap();
      }
    });
  }

  getCurrentScale(): number {
    return this.currentScale;
  }

  getBaseScale(): number {
    return this.baseScale;
  }

  getViewportScale(): number {
    const scales = this.getFitScales();
    if (!scales) return this.baseScale;
    return Math.min(scales.widthScale, scales.heightScale);
  }

  /** Returns a normalized user-facing zoom scale. Canvas safety is applied while rendering. */
  getSafeScale(scale: number, baseScale = this.baseScale): number {
    const minimumScale = Math.min(0.2, baseScale);
    const normalizedScale = Number.isFinite(scale)
      ? Math.max(minimumScale, Math.min(PDF_RENDER_LIMITS.maxScale, scale))
      : Math.max(minimumScale, this.currentScale);
    const plan = this.createRenderPlan(normalizedScale);
    if (plan?.budgetClamped) {
      const signature = [
        Math.round(plan.sourceWidth),
        Math.round(plan.sourceHeight),
        plan.requestedScale.toFixed(6),
        plan.effectiveScale.toFixed(6),
      ].join(':');
      if (signature !== this.lastBudgetClampSignature) {
        this.lastBudgetClampSignature = signature;
        ClientLoggingService.log('WARN', '[PdfRenderService] Render request clamped to canvas budget', {
          sourceWidth: Math.round(plan.sourceWidth),
          sourceHeight: Math.round(plan.sourceHeight),
          requestedScale: plan.requestedScale,
          effectiveScale: plan.effectiveScale,
          outputWidth: plan.outputWidth,
          outputHeight: plan.outputHeight,
          outputPixels: plan.outputPixels,
          estimatedCanvasBytes: plan.estimatedCanvasBytes,
        });
      }
    }
    return normalizedScale;
  }

  /** Highest scale PDF.js can render sharply without exceeding the canvas budget. */
  getMaximumRenderScale(): number {
    return this.createRenderPlan(PDF_RENDER_LIMITS.maxScale)?.effectiveScale ?? PDF_RENDER_LIMITS.maxScale;
  }

  private createRenderPlan(scale: number): PdfRenderPlan | null {
    if (!this.currentPdfPage) return null;
    const sourceViewport = this.currentPdfPage.getViewport({ scale: 1.0 });
    return planPdfRender(sourceViewport.width, sourceViewport.height, scale);
  }

  /** Clockwise display rotation of the current page, from its PDF /Rotate value (Issue #1747). */
  getPageRotation(): number {
    return this.currentPdfPage?.rotate ?? 0;
  }

  /** Size of the current page in PDF points before its rotation is applied (Issue #1747). */
  getUnrotatedPageSize(): { width: number; height: number } | null {
    if (!this.currentPdfPage) {
      return null;
    }
    const viewport = this.currentPdfPage.getViewport({ scale: 1.0, rotation: 0 });
    return { width: viewport.width, height: viewport.height };
  }

  /** Returns the original (scale=1.0) page dimensions in PDF points, or null. */
  getOriginalPageDimensions(): { width: number; height: number } | null {
    if (!this.currentPdfPage) {
      return null;
    }
    const viewport = this.currentPdfPage.getViewport({ scale: 1.0 });
    return { width: viewport.width, height: viewport.height };
  }

  /** Delegates to the shared PdfCacheService to invalidate a specific page. */
  invalidatePageCache(page: ArchitecturalPlanPage): void {
    this.cancelCurrentRender();
    if (this.retainedPage && PdfRenderService.samePage(this.retainedPage, page)) this.releaseRetainedPage();
    this.pdfCacheService.invalidatePageCache(page);
    this.currentPdf = null;
    this.currentPdfPage = null;
  }

  private releaseRetainedPage(): void {
    if (!this.retainedPage) return;
    this.pdfCacheService.releaseParsedPage(this.retainedPage);
    this.retainedPage = null;
  }

  private static samePage(a: ArchitecturalPlanPage, b: ArchitecturalPlanPage): boolean {
    return PdfCacheService.buildCacheKey(a) === PdfCacheService.buildCacheKey(b);
  }

  ngOnDestroy(): void {
    this.cancelCurrentRender();
    this.releaseRetainedPage();
  }

  async getPdfUrl(): Promise<string | null> {
    if (!this.currentPdf) {
      return null;
    }

    try {
      const pdfData = await this.currentPdf.getData();
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error creating PDF URL:', error);
      return null;
    }
  }

  private getFocalPointInfo(
    focalXScreen?: number,
    focalYScreen?: number,
    unscaledCx?: number,
    unscaledCy?: number
  ): FocalPointInfo | null {
    if (!this.container || !this.canvas) return null;
    const canvasRect = this.canvas.getBoundingClientRect();
    const viewportCenter = scrollportCenter(this.container);

    const targetX = focalXScreen !== undefined ? focalXScreen : viewportCenter.x;
    const targetY = focalYScreen !== undefined ? focalYScreen : viewportCenter.y;

    const cx = unscaledCx !== undefined ? unscaledCx : targetX - canvasRect.left;
    const cy = unscaledCy !== undefined ? unscaledCy : targetY - canvasRect.top;

    return { targetX, targetY, cx, cy, oldScale: this.currentScale };
  }

  private preserveFocalPoint(focalInfo: FocalPointInfo | null, newScale: number): void {
    if (!this.container || !this.canvas || !focalInfo) return;

    const scaleRatio = newScale / focalInfo.oldScale;

    const target = { x: focalInfo.targetX, y: focalInfo.targetY };
    const canvasPoint = { x: focalInfo.cx, y: focalInfo.cy };
    const canvasRect = this.canvas.getBoundingClientRect();
    const unclamped = {
      left: this.container.scrollLeft + canvasRect.left + canvasPoint.x * scaleRatio - target.x,
      top: this.container.scrollTop + canvasRect.top + canvasPoint.y * scaleRatio - target.y,
    };
    const maxSL = Math.max(0, this.container.scrollWidth - this.container.clientWidth);
    const maxST = Math.max(0, this.container.scrollHeight - this.container.clientHeight);
    const clamped = anchoredScrollPosition(this.container, this.canvas, canvasPoint, target, scaleRatio);

    const overshootX = unclamped.left - clamped.left;
    const overshootY = unclamped.top - clamped.top;

    ClientLoggingService.log('DEBUG', '[Zoom] preserveFocalPoint:', {
      scrollBefore: { left: Math.round(this.container.scrollLeft), top: Math.round(this.container.scrollTop) },
      unclamped: { left: Math.round(unclamped.left), top: Math.round(unclamped.top) },
      clamped: { left: Math.round(clamped.left), top: Math.round(clamped.top) },
      maxScroll: { left: maxSL, top: maxST },
      overshoot: { x: Math.round(overshootX), y: Math.round(overshootY) },
    });

    // Phase 1: Instantly scroll to the clamped position.
    // This securely snaps the physical layout to the nearest valid position.
    const originalScrollBehavior = this.container.style.scrollBehavior;
    this.container.style.scrollBehavior = 'auto';
    this.container.scrollLeft = clamped.left;
    this.container.scrollTop = clamped.top;
    this.container.style.scrollBehavior = originalScrollBehavior;

    // Phase 2: If there was an overshoot, the browser clamping caused an abrupt visual jump.
    // We visually restore the overshoot using a CSS transform, then smoothly glide back to 0.
    if (Math.abs(overshootX) > 0.5 || Math.abs(overshootY) > 0.5) {
      const canvasEl = this.canvas;

      // Instantly apply the overshoot offset
      canvasEl.style.transition = 'none';
      canvasEl.style.transform = `translate3d(${-overshootX}px, ${-overshootY}px, 0)`;

      // Force a synchronous browser reflow to ensure the instant transform is applied
      void canvasEl.offsetHeight;

      const currentAnimId = ++this.zoomAnimationId;

      // A setTimeout of 10ms defers the execution to a later macrotask.
      // This is a reliable heuristic that allows the browser's main thread to
      // process the synchronous layout and paint the initial instant transform
      // before starting the CSS transition, preventing abrupt skipped animations.
      setTimeout(() => {
        // Turn on smooth transitions and glide back into the clamped position
        canvasEl.style.transition = 'transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        canvasEl.style.transform = 'translate3d(0px, 0px, 0)';

        // Clean up inline styles after the animation
        setTimeout(() => {
          // Only clean up if another gesture hasn't started
          if (this.zoomAnimationId === currentAnimId) {
            canvasEl.style.transition = 'none';
            canvasEl.style.transform = '';
          }
        }, 260);
      }, 10);
    }
  }

  private centerView(): void {
    if (this.container) {
      const canvasRect = this.canvas.getBoundingClientRect();

      const centerX = (canvasRect.width - this.container.clientWidth) / 2;
      const centerY = (canvasRect.height - this.container.clientHeight) / 2;

      const paddingOffset = PdfRenderService.CENTERING_PADDING_PX;
      if (canvasRect.width > this.container.clientWidth) {
        this.container.scrollLeft = Math.max(0, centerX + paddingOffset);
      }
      if (canvasRect.height > this.container.clientHeight) {
        this.container.scrollTop = Math.max(0, centerY + paddingOffset);
      }
    }
  }
}
